import React from 'react'
import { useStyletron } from 'styletron-react'
import { lightTheme } from '@/customTheme'

export interface SegmentedControlOption<T extends string | number> {
  value: T
  /** Visible text in 'pill'/'stacked'. In 'icons' it is not rendered, and
   *  becomes the segment's aria-label instead (icon-only buttons still need an
   *  accessible name). */
  label: string
  disabled?: boolean
  /** Required when variant="stacked" or "icons". */
  Icon?: (p: { size?: number; color?: string }) => JSX.Element
}

/** Active-segment elevation in the stacked variant (Figma effect "Size 1"). */
const STACKED_ACTIVE_SHADOW = '0px 0px 6px 0px rgba(0, 0, 0, 0.08)'
/** Active-segment elevation in the icons variant (Figma "Switcher with icons"). */
const ICONS_ACTIVE_SHADOW = '0px 0px 3px 0px rgba(0, 0, 0, 0.08)'

export interface SegmentedControlProps<T extends string | number> {
  /** Title shown above the track. */
  label?: string
  options: SegmentedControlOption<T>[]
  value: T
  onToggle: (value: T) => void
  disabled?: boolean
  /** 'pill' (default): fully-rounded track, label-only segments, active segment
   *  gets a white fill.
   *  'stacked': rounded-rect track, icon above label per segment, active segment
   *  gets a white raised card.
   *  'icons': rounded-rect track, icon-only segments (label becomes the
   *  aria-label), active segment gets a white raised card. Figma "Switcher with
   *  icons". */
  variant?: 'pill' | 'stacked' | 'icons'
  /** Fires with the segment under the pointer, and `null` when it leaves the
   *  track — for hover previews anchored to a segment. */
  onHoverChange?: (value: T | null) => void
  /** Ref to the track. Each segment carries `data-value`, so callers can
   *  locate a segment's rect (e.g. to position a hover preview). */
  trackRef?: React.Ref<HTMLDivElement>
}

function SegmentedControl<T extends string | number>({
  label,
  options,
  value,
  onToggle,
  disabled = false,
  variant = 'pill',
  onHoverChange,
  trackRef,
}: SegmentedControlProps<T>) {
  const [css] = useStyletron()
  const isStacked = variant === 'stacked'
  const isIcons = variant === 'icons'
  // Both non-pill variants share the rounded-rect track + raised active card;
  // they differ only in track padding, shadow blur, and whether a label renders.
  const isCard = isStacked || isIcons

  const trackClassName = css(
    isCard
      ? {
          display: 'flex',
          alignItems: 'center',
          background: lightTheme.colors.grayScale50,
          borderRadius: '12px',
          padding: isIcons ? '6px' : '4px',
          width: '100%',
        }
      : {
          background: '#e5e5e5',
          borderRadius: '100px',
          display: 'flex',
          height: '48px',
          padding: '4px',
          width: '100%',
          gap: '4px',
        },
  )

  const segmentClassName = (isActive: boolean, isDisabled: boolean) =>
    css(
      isCard
        ? {
            flex: '1 0 0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            padding: '6px 8px',
            border: 'none',
            borderRadius: '8px',
            background: isActive ? lightTheme.colors.white : 'transparent',
            boxShadow: isActive ? (isIcons ? ICONS_ACTIVE_SHADOW : STACKED_ACTIVE_SHADOW) : 'none',
            cursor: isDisabled ? 'default' : 'pointer',
            opacity: isDisabled ? lightTheme.opacity.disabled : lightTheme.opacity.active,
            color: isActive ? lightTheme.colors.blackGray : lightTheme.colors.text.text_600_default,
            ':hover': !isDisabled && !isActive ? { color: lightTheme.colors.blackGray } : undefined,
          }
        : {
            background: isActive ? lightTheme.colors.white : 'transparent',
            borderRadius: '100px',
            flex: '1 0 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: isDisabled ? 'default' : 'pointer',
            opacity: isDisabled ? lightTheme.opacity.disabled : lightTheme.opacity.active,
            border: 'none',
            padding: '8px 12px',
            transition: 'background 0.15s ease',
            ':focus-visible': {
              outline: '2px solid rgba(124,58,237,0.8)',
              outlineOffset: '2px',
            },
          },
    )

  const textClassName = () =>
    css(
      isStacked
        ? {
            fontSize: '9px',
            lineHeight: '16px',
            fontWeight: 600,
            letterSpacing: '0.09px',
            color: 'inherit',
          }
        : {
            ...lightTheme.typography.Small14medium,
            color: lightTheme.colors.blackGray,
            textAlign: 'center',
            whiteSpace: 'nowrap',
          },
    )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
      {label ? (
        <span style={{ ...lightTheme.typography.Small14bold, color: lightTheme.colors.blackGray }}>
          {label}
        </span>
      ) : null}
      <div
        ref={trackRef}
        className={trackClassName}
        onMouseLeave={onHoverChange ? () => onHoverChange(null) : undefined}
      >
        {options.map((option) => {
          const isActive = option.value === value
          const isDisabled = disabled || option.disabled === true
          const iconColor = isStacked
            ? 'currentColor'
            : isActive
              ? lightTheme.colors.blackGray
              : lightTheme.colors.grayScale400
          return (
            <button
              key={String(option.value)}
              type="button"
              className={segmentClassName(isActive, isDisabled)}
              data-value={option.value}
              onClick={() => !isDisabled && onToggle(option.value)}
              onMouseEnter={onHoverChange ? () => onHoverChange(option.value) : undefined}
              disabled={isDisabled}
              aria-pressed={isActive}
              // Icon-only segments render no text, so the label carries the
              // accessible name instead of being visible.
              aria-label={isIcons ? option.label : undefined}
            >
              {option.Icon ? <option.Icon size={24} color={iconColor} /> : null}
              {isIcons ? null : <span className={textClassName()}>{option.label}</span>}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default SegmentedControl
